import {
  Component,
  Inject,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChildren,
  ViewEncapsulation,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Logger, LoggerFactory } from '../../core/logger.service';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { SnackBarService } from '../../core/snack-bar.service';
import { AccountService } from '../../account/account.service';
import {
  AudienceFilter,
  AudienceFilterApp,
  AudienceFilterConfig,
  AudienceFilterFrequencyOperator,
  AudienceFilterInputType,
  AudienceFilterOperator,
  AudienceFilterSalesOperator,
  AudienceFilterTimeOperator,
  AudienceFilterTimeValue,
  AudienceFilterType,
  PurchasePropertiesModel,
  TimeFrame,
  WeatherAppsKeyValuePair,
  WeatherPropertiesModel,
  WeatherTypeModel,
} from '../../audience/components/audience.model';
import AudienceHelpers from '../../audience/audience-helpers';
import { FilterProperty } from '../../search/filter-property';
import { SelectionsService } from '../../shared/services/selections.service';
import { VisitsService } from '../../visits/visits.service';
import { IntegrationsService } from '../../shared/services/integrations.service';
import { WidgetSimpleInfo } from '../../campaign/widgets.model';
import { LanguageService } from 'app/core/language.service';
import TimeUtils from 'app/shared/utils/time-utils';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter } from '@angular/material-moment-adapter';
import { FORMATS_DASH_NUMBER } from '../../shared/utils/format-data-picker';
import { Subscription } from 'rxjs';
import { AudienceService } from 'app/audience/audience.service';
import { Note } from 'app/shared/note-info/note-info';
import { MatMenuTrigger } from '@angular/material/menu';
import { Account } from 'app/back-office/account.model';

@Component({
  selector: 'app-filter-item-dialog',
  templateUrl: './filter-item-dialog.component.html',
  styleUrls: ['./filter-item-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [
    { provide: MAT_DATE_LOCALE, useValue: 'sv_SE' },
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
    },
    { provide: MAT_DATE_FORMATS, useValue: FORMATS_DASH_NUMBER },
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class FilterItemDialogComponent implements OnInit, OnDestroy {
  @ViewChildren(MatMenuTrigger) menuTriggers: QueryList<MatMenuTrigger>;
  public readonly LOGGER: Logger;
  public updateInProgress = false;
  public updateFormGroup: UntypedFormGroup;

  public dialogTitle: string;
  public isNewFilter = true;
  public audienceFilter: AudienceFilter;
  public FilterInputTypes = AudienceFilterInputType;
  public AudienceFilterTypes = AudienceFilterType;
  public AudienceFilterApp = AudienceFilterApp;
  public showOperator = true;
  public filterInputType: AudienceFilterInputType;
  private getAccountSub: Subscription;
  public account: Account;
  private subscriptionArray: Subscription[] = [];

  public daysFC = new UntypedFormControl(30, [Validators.required, Validators.min(1), Validators.max(365)]);
  public startDateFC = new UntypedFormControl(TimeUtils.getDateAddtoDate(-30, 'days'), [Validators.required]);
  public endDateFC = new UntypedFormControl(TimeUtils.getDateToDate(), [Validators.required]);
  public daysStartFC = new UntypedFormControl(1, [Validators.required, Validators.min(1), Validators.max(365)]);
  public daysEndFC = new UntypedFormControl(5, [Validators.required, Validators.min(1), Validators.max(365)]);
  public sumStartFC = new UntypedFormControl(0, [Validators.required, Validators.min(0)]);
  public sumEndFC = new UntypedFormControl(1, [Validators.required, Validators.min(1)]);
  public frequencyValue = new UntypedFormControl(1, [Validators.required, Validators.min(1)]);
  public frequencyOperator: string;
  public salesOperator: string;
  public hasAppliedCoupon: boolean;

  public timeframeSupported = false;
  public AudienceFilterTimeOperators = AudienceFilterTimeOperator;
  public AudienceFilterSalesOperator = AudienceFilterSalesOperator;
  public SalesOperators = [
    AudienceFilterSalesOperator.Exactly,
    AudienceFilterSalesOperator.LessThan,
    AudienceFilterSalesOperator.MoreThan,
    AudienceFilterSalesOperator.Between,
    AudienceFilterSalesOperator.Any,
  ];

  public timeframeValueError: string;
  public salesValueError: string;
  public frequencyValueError: string;
  public timeframeValue = {} as AudienceFilterTimeValue;

  public filterOperator: AudienceFilterOperator;
  public AudienceFilterOperator = AudienceFilterOperator;
  public filterValueString: string | any;
  public filterValueTagString: string;
  public filterValueArray: Array<any>;
  public audienceValueError = false;
  public audienceOperatorError = false;

  public timeframeOperator: AudienceFilterTimeOperator;

  public choosableValueOptions: Array<{ key: any; value: any }>;
  public choosableValueTagOptions: Array<{ key: any; value: any }>;
  public isCountryFilter = false;
  public allWidgets: Array<WidgetSimpleInfo>;
  public loadingOptions = false;

  public weatherTypeOptions = Object.keys(WeatherTypeModel).filter((key) => isNaN(Number(key)));
  public timeFrame: string;
  public weatherApps: WeatherAppsKeyValuePair[] = [];
  public audienceFilters: Array<AudienceFilter>;

  constructor(
    private dialogRef: MatDialogRef<FilterItemDialogComponent>,
    private snackbarService: SnackBarService,
    private readonly accountService: AccountService,
    protected readonly loggerFactory: LoggerFactory,
    private readonly selectionsService: SelectionsService,
    private readonly visitsService: VisitsService,
    private readonly integrationsService: IntegrationsService,
    private languageService: LanguageService,
    private dateAdapter: DateAdapter<any>,
    private audienceService: AudienceService,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      audienceFilter: AudienceFilter;
      newFilterConfig: AudienceFilterConfig;
      allWidgets: Array<WidgetSimpleInfo>;
      audienceFilters: Array<AudienceFilter>;
    },
  ) {
    this.LOGGER = loggerFactory.get('AudienceChartChangeGoalNameDialogComponent');
    this.isNewFilter = !!data.audienceFilter;
    this.allWidgets = data.allWidgets;
    this.audienceFilters = data.audienceFilters;
    this.initializeAudienceFilter(
      data.audienceFilter ? JSON.parse(JSON.stringify(data.audienceFilter)) : undefined,
      data.newFilterConfig,
    );
    this.dateAdapter.setLocale(this.languageService.getLocaleFromCurrentLanguage());
  }

  public filterOperatorUpdated(operator: AudienceFilterOperator) {
    this.filterOperator = operator;
    this.filterInputType = AudienceHelpers.getInputTypeForFilterAndOperator(
      this.audienceFilter.type,
      this.filterOperator,
      this.audienceFilter.app,
    );
  }

  public filterTimeframeOperatorUpdated(timeframeOperator: AudienceFilterTimeOperator) {
    this.timeframeOperator = timeframeOperator;
    if (!this.timeframeValue) {
      this.timeframeValue = {} as AudienceFilterTimeValue;
    }
  }

  public saveFilterItem(): void {
    let listId;
    if (this.isAppTags && this.filterValueTagString) {
      listId = this.filterValueString;
      this.filterValueString = this.filterValueTagString;
    }
    let skipTranslatingValue = false;
    if (
      this.audienceFilter.weatherProperties.weatherType === WeatherTypeModel.Temperature &&
      this.audienceFilter.type === AudienceFilterType.Weather
    ) {
      this.audienceFilter.type = AudienceFilterType.Temperature;
    }
    if (AudienceFilterInputType.MultiChoiceDropdown === this.filterInputType) {
      this.audienceFilter.value = this.filterValueArray.join(',');
    } else {
      this.audienceFilter.value = this.filterValueString;
    }
    if (this.audienceFilter.type === AudienceFilterType.Weather) {
      this.audienceFilter.value = this.audienceFilter.weatherProperties.weatherType;
    } else if (this.audienceFilter.type === AudienceFilterType.Temperature) {
      const minTemp = this.audienceFilter.weatherProperties.minTemperature;
      const maxTemp = this.audienceFilter.weatherProperties.maxTemperature;
      this.audienceFilter.filterConfig.type = AudienceFilterType.Temperature;
      this.audienceFilter.filterConfig.titleKey = 'audience.type-titles.temperature';
      this.audienceFilter.value = `Min ${minTemp}°C max ${maxTemp}°C`;
    }

    this.audienceFilter.operator = this.filterOperator;
    if (
      [AudienceFilterType.Goals, AudienceFilterType.Purchase].includes(this.audienceFilter.type) &&
      this.frequencyValue.invalid
    ) {
      this.frequencyValueError = 'generic.validation.error-wrong-number';
      return;
    }
    this.audienceFilter.frequencyType =
      this.filterOperator !== AudienceFilterOperator.NotEquals ? this.frequencyOperator : null;
    this.audienceFilter.frequencyValue =
      this.filterOperator !== AudienceFilterOperator.NotEquals ? this.frequencyValue.value : null;
    if (this.filterInputType !== AudienceFilterInputType.TextField) {
      if (
        !this.audienceFilter.filterConfig?.skipValueSelection &&
        (!this.audienceFilter.value ||
          this.audienceFilter.value === '' ||
          (this.isAppTags && !this.filterValueTagString))
      ) {
        this.audienceValueError = true;
        return;
      }
    } else {
      if (!this.audienceFilter.filterTranslations) {
        this.audienceFilter.filterTranslations = {};
      }
      this.audienceFilter.filterTranslations.value = this.audienceFilter.value;
      skipTranslatingValue = true;
      if (this.isAppTags && !this.filterValueString) {
        this.audienceValueError = true;
        return;
      }
    }
    if (!this.audienceFilter.operator) {
      this.audienceOperatorError = true;
      return;
    }
    if (this.choosableValueOptions) {
      const foundOption =
        this.choosableValueOptions.find((o) => o.key === this.audienceFilter.value) ||
        (this.choosableValueTagOptions &&
          this.choosableValueTagOptions.find((o) => o.key === this.audienceFilter.value));
      if (foundOption) {
        if (!this.audienceFilter.filterTranslations) {
          this.audienceFilter.filterTranslations = {};
        }
        this.audienceFilter.filterTranslations.value = foundOption.value;
        skipTranslatingValue = true;
      }
    }
    if (this.timeframeSupported) {
      if (this.timeframeOperator === AudienceFilterTimeOperator.AnyTime) {
        this.audienceFilter.timeOperator = undefined;
        this.audienceFilter.timeValue = undefined;
      } else if (this.timeframeOperator === AudienceFilterTimeOperator.TimeWindow) {
        if (this.daysFC.invalid) {
          this.timeframeValueError = 'audience.timeframe.invalid-days';
          return;
        }
        this.audienceFilter.timeOperator = AudienceFilterTimeOperator.TimeWindow;
        this.audienceFilter.timeValue = JSON.stringify({
          days: this.daysFC.value,
        } as AudienceFilterTimeValue);
      } else if (this.timeframeOperator === AudienceFilterTimeOperator.SinceDate) {
        if (this.startDateFC.invalid) {
          this.timeframeValueError = 'audience.timeframe.invalid-dates';
          return;
        }
        this.audienceFilter.timeOperator = AudienceFilterTimeOperator.SinceDate;
        this.audienceFilter.timeValue = JSON.stringify({
          startDate: TimeUtils.getFormattedDate(this.startDateFC.value),
        } as AudienceFilterTimeValue);
      } else if (this.timeframeOperator === AudienceFilterTimeOperator.BetweenDates) {
        if (this.startDateFC.invalid || this.endDateFC.invalid) {
          this.timeframeValueError = 'audience.timeframe.invalid-dates';
          return;
        }
        this.audienceFilter.timeOperator = AudienceFilterTimeOperator.BetweenDates;
        this.audienceFilter.timeValue = JSON.stringify({
          startDate: TimeUtils.getFormattedDate(this.startDateFC.value),
          endDate: TimeUtils.getFormattedDate(this.endDateFC.value),
        } as AudienceFilterTimeValue);
      } else if (this.timeframeOperator === AudienceFilterTimeOperator.BetweenDaysAgo) {
        if (this.daysStartFC.invalid || this.daysEndFC.invalid) {
          this.timeframeValueError = 'audience.timeframe.invalid-days';
          return;
        }
        this.audienceFilter.timeOperator = AudienceFilterTimeOperator.BetweenDaysAgo;
        this.audienceFilter.timeValue = JSON.stringify({
          days: this.daysStartFC.value,
          upperDays: this.daysEndFC.value,
        } as AudienceFilterTimeValue);
      }
      this.timeframeValueError = undefined;
    }
    if (this.audienceFilter.type === AudienceFilterType.Purchase) {
      if (this.salesOperator !== AudienceFilterSalesOperator.Between && this.sumStartFC.invalid) {
        this.salesValueError = 'generic.validation.error-wrong-number';
        return;
      } else if (
        this.salesOperator === AudienceFilterSalesOperator.Between &&
        (this.sumStartFC.invalid || this.sumEndFC.invalid)
      ) {
        this.salesValueError = 'generic.validation.error-wrong-number';
        return;
      }
      if (!this.audienceFilter.purchaseProperties) {
        this.audienceFilter.purchaseProperties = {} as PurchasePropertiesModel;
      }
      this.audienceFilter.purchaseProperties.operator = this.salesOperator;
      this.audienceFilter.purchaseProperties.value = this.sumStartFC.value;
      this.audienceFilter.purchaseProperties.upperValue = this.sumEndFC.value;
      this.audienceFilter.purchaseProperties.appliedCoupon = this.hasAppliedCoupon;
      if (
        (this.salesOperator === AudienceFilterSalesOperator.Any ||
          this.filterOperator === AudienceFilterOperator.NotEquals) &&
        this.audienceFilter.purchaseProperties
      ) {
        this.audienceFilter.purchaseProperties = null;
      }
    }
    if (this.audienceFilter.weatherProperties) {
      this.audienceFilter.weatherProperties = this.initializeWeatherProperties(this.audienceFilter.weatherProperties);
    } else {
      this.audienceFilter.weatherProperties = null;
    }
    if (this.audienceFilter.weatherProperties?.specificDay) {
      this.audienceFilter.weatherProperties.specificDay = TimeUtils.getFormattedDate(
        this.audienceFilter.weatherProperties.specificDay,
      );
    }
    this.audienceValueError = false;
    this.dialogRef.close({
      audienceFilter: this.audienceFilter,
      skipTranslatingValue: skipTranslatingValue,
      listId,
    });
  }

  private initializeAudienceFilter(existingFilter: AudienceFilter, newFilterConfig: AudienceFilterConfig) {
    if (existingFilter) {
      this.audienceFilter = existingFilter;
    } else if (newFilterConfig) {
      this.audienceFilter = AudienceHelpers.audienceConfigToFilter(newFilterConfig);
    } else {
      throw new Error('Existing filter should be sent to dialog or a type for a new filter');
    }

    this.audienceFilter.weatherProperties = this.initializeWeatherProperties(this.audienceFilter.weatherProperties) ?? {
      weatherType: WeatherTypeModel.Temperature,
      preferedApp: '',
      specificDay: null,
      daysForward: null,
      checkNextWeekend: false,
      checkToday: false,
      checkTomorrow: false,
      inDays: null,
      minTemperature: null,
      maxTemperature: null,
      customLongitude: null,
      customLatitude: null,
      minWindspeed: null,
      maxWindspeed: null,
    };

    this.getAccountSub = this.accountService.getCurrentAccount().subscribe((account) => {
      this.account = account;
      this.loadChoosableValueOptions();
    });
    this.showOperator = this.shouldOperatorBeDisplayed(this.audienceFilter.type);
    this.filterOperator = this.audienceFilter.operator;
    this.filterInputType = AudienceHelpers.getInputTypeForFilterAndOperator(
      this.audienceFilter.type,
      this.audienceFilter.operator,
      this.audienceFilter.app,
    );
    this.frequencyOperator = this.audienceFilter.frequencyType || this.audienceFilter.filterConfig.defaultFrequencyType;
    this.frequencyValue.setValue(
      this.audienceFilter.frequencyValue || this.audienceFilter.filterConfig.defaultFrequencyValue,
    );
    this.setSalesValue();
    this.parseTimeFilteringIfNeeded();
    this.parseFilterValueToCorrectType();
    if (this.audienceFilter.type === AudienceFilterType.Purchase) {
      this.filterValueString = this.account?.metricsGoalNames[0];
    }
    this.disableOptionIfBooleanDropdown();
  }

  private initializeWeatherProperties(
    weatherProperties: WeatherPropertiesModel | undefined,
  ): WeatherPropertiesModel | null {
    if (!weatherProperties) {
      return null;
    }
    this.timeFrame = weatherProperties.specificDay
      ? TimeFrame.SpecificDay
      : weatherProperties.daysForward
        ? TimeFrame.DaysForward
        : weatherProperties.checkNextWeekend
          ? TimeFrame.CheckNextWeekend
          : weatherProperties.checkToday
            ? TimeFrame.CheckToday
            : weatherProperties.checkTomorrow
              ? TimeFrame.CheckTomorrow
              : weatherProperties.inDays
                ? TimeFrame.InDays
                : TimeFrame.Now;
    return {
      weatherType: weatherProperties.weatherType || WeatherTypeModel.Temperature,
      preferedApp: weatherProperties.preferedApp || '',
      specificDay: weatherProperties.specificDay || null,
      daysForward: weatherProperties.daysForward ?? null,
      checkNextWeekend: weatherProperties.checkNextWeekend ?? false,
      checkToday: weatherProperties.checkToday ?? false,
      checkTomorrow: weatherProperties.checkTomorrow ?? false,
      inDays: weatherProperties.inDays ?? null,
      minTemperature: weatherProperties.minTemperature ?? null,
      maxTemperature: weatherProperties.maxTemperature ?? null,
      minWindspeed: weatherProperties.minWindspeed ?? null,
      maxWindspeed: weatherProperties.maxWindspeed ?? null,
      customLongitude: weatherProperties.customLongitude ?? null,
      customLatitude: weatherProperties.customLatitude ?? null,
    };
  }

  private shouldOperatorBeDisplayed(filterType: AudienceFilterType): boolean {
    if (
      filterType === AudienceFilterType.Identified ||
      filterType === AudienceFilterType.LoggedIn ||
      filterType === AudienceFilterType.Weather ||
      filterType === AudienceFilterType.Temperature
    ) {
      return false;
    }
    return true;
  }

  private parseTimeFilteringIfNeeded() {
    this.timeframeSupported =
      this.audienceFilter.type === AudienceFilterType.Goals || this.audienceFilter.type === AudienceFilterType.Purchase;
    if (this.timeframeSupported && !!this.audienceFilter.timeValue && this.audienceFilter.timeValue !== '') {
      this.timeframeOperator = this.audienceFilter.timeOperator
        ? this.audienceFilter.timeOperator
        : AudienceFilterTimeOperator.AnyTime;
      this.timeframeValue = JSON.parse(this.audienceFilter.timeValue as string);
      if (this.timeframeOperator === AudienceFilterTimeOperator.TimeWindow && this.timeframeValue.days) {
        this.daysFC.setValue(this.timeframeValue.days);
      } else if (this.timeframeOperator === AudienceFilterTimeOperator.SinceDate && this.timeframeValue.startDate) {
        this.startDateFC.setValue(TimeUtils.getMoment(this.timeframeValue.startDate));
      } else if (
        this.timeframeOperator === AudienceFilterTimeOperator.BetweenDates &&
        this.timeframeValue.startDate &&
        this.timeframeValue.endDate
      ) {
        this.startDateFC.setValue(TimeUtils.getMoment(this.timeframeValue.startDate));
        this.endDateFC.setValue(TimeUtils.getMoment(this.timeframeValue.endDate));
      } else if (this.timeframeOperator === AudienceFilterTimeOperator.BetweenDaysAgo && this.timeframeValue) {
        this.daysStartFC.setValue(this.timeframeValue.days);
        this.daysEndFC.setValue(this.timeframeValue.upperDays);
      }
    } else {
      this.timeframeOperator = AudienceFilterTimeOperator.AnyTime;
      this.resetTimeframeValues();
    }
  }

  private resetTimeframeValues() {
    this.timeframeValue = {
      days: undefined,
      startDate: undefined,
      endDate: undefined,
    };
  }

  private parseFilterValueToCorrectType() {
    if (this.filterInputType === AudienceFilterInputType.MultiChoiceDropdown) {
      if (this.audienceFilter.value) {
        this.filterValueArray = this.audienceFilter.value.replace(/ /g, '').split(',');
      } else {
        this.filterValueArray = [];
      }
    } else if (this.filterInputType === AudienceFilterInputType.TextField) {
      if (this.audienceFilter.filterTranslations) {
        this.filterValueString = this.audienceFilter.filterTranslations.value;
      }
    } else {
      this.filterValueString = this.audienceFilter.value;
    }
  }

  public setSalesValue(): void {
    if (this.audienceFilter.type !== AudienceFilterType.Purchase) return;
    if (this.audienceFilter.purchaseProperties) {
      this.salesOperator =
        this.audienceFilter.purchaseProperties?.operator || this.audienceFilter.filterConfig.defaultSalesType;
    } else {
      this.salesOperator = AudienceFilterSalesOperator.Any;
    }
    this.hasAppliedCoupon = this.audienceFilter.purchaseProperties?.appliedCoupon;
    if (this.audienceFilter.purchaseProperties?.value)
      this.sumStartFC.setValue(this.audienceFilter.purchaseProperties?.value);
    if (this.audienceFilter.purchaseProperties?.upperValue)
      this.sumEndFC.setValue(this.audienceFilter.purchaseProperties?.upperValue);
  }

  ngOnInit(): void {}

  public closeDialog(): void {
    this.dialogRef.close();
  }

  private loadChoosableValueOptions() {
    if (this.audienceFilter.type === AudienceFilterType.Consent) {
      this.loadConsentListToOptions();
    } else if (
      this.audienceFilter.type === AudienceFilterType.Goals ||
      this.audienceFilter.type === AudienceFilterType.Purchase
    ) {
      this.loadGoalsListToOptions();
    } else if (this.audienceFilter.type === AudienceFilterType.Tags && !this.isAppTags) {
      this.loadTagsListToOptions();
    } else if (this.audienceFilter.type === AudienceFilterType.InterestProfiles) {
      this.loadInterestProfilesListToOptions();
    } else if (this.audienceFilter.type === AudienceFilterType.Country) {
      this.loadCountryListToOptions();
    } else if (
      this.audienceFilter.type === AudienceFilterType.Weather ||
      this.audienceFilter.type === AudienceFilterType.Temperature
    ) {
      this.loadWeatherAppsOptions();
    } else if (
      this.audienceFilter.type === AudienceFilterType.AppList ||
      this.audienceFilter.type === AudienceFilterType.AppSegment ||
      this.audienceFilter.type === AudienceFilterType.AppLabels ||
      this.isAppTags
    ) {
      if (this.audienceFilter.filterConfig?.skipValueSelection) {
        this.loadValueFromIntegrationConfig();
      } else {
        this.loadAppsOptions();
      }
    }
  }

  private loadConsentListToOptions() {
    this.loadingOptions = true;
    this.selectionsService.getVersions().then((versions) => {
      const choosableValueOptions = [] as Array<{ key: any; value: any }>;
      versions.forEach((consentVersion: FilterProperty) => {
        choosableValueOptions.push({
          key: consentVersion.id,
          value: consentVersion.displayName,
        });
      });
      this.choosableValueOptions = choosableValueOptions;
      this.loadingOptions = false;
    });
  }

  private loadGoalsListToOptions() {
    this.loadingOptions = true;
    this.selectionsService.getGoals().subscribe(
      (response: FilterProperty[]) => {
        const choosableValueOptions = [] as Array<{ key: any; value: any }>;
        if (response && response.length > 0) {
          response.forEach((goal: FilterProperty) => {
            choosableValueOptions.push({
              key: goal.id,
              value: this.visitsService.getGoalDisplayName(goal.displayName, this.allWidgets),
            });
          });
        }
        this.choosableValueOptions = choosableValueOptions.sort((a, b) => {
          const name1 = a.value.toLowerCase();
          const name2 = b.value.toLowerCase();
          return name1 > name2 ? 1 : -1;
        });
        this.loadingOptions = false;
      },
      () => {
        this.loadingOptions = false;
        this.snackbarService.error('generic.error');
      },
    );
  }

  private async loadTagsListToOptions() {
    this.loadingOptions = true;
    const response = await this.selectionsService.getLabelsAsync();
    const choosableValueOptions = [] as Array<{ key: any; value: any }>;
    if (response && response.length > 0) {
      response.forEach((item: FilterProperty) => {
        choosableValueOptions.push({ key: item.id, value: item.displayName });
      });
    }
    this.choosableValueOptions = choosableValueOptions;
    this.loadingOptions = false;
  }

  private async loadInterestProfilesListToOptions() {
    this.loadingOptions = true;
    const response = await this.selectionsService.getInterestProfilesAsync();
    const choosableValueOptions = [] as Array<{ key: any; value: any }>;
    if (response && response.length > 0) {
      response.forEach((item: FilterProperty) => {
        choosableValueOptions.push({ key: item.id, value: item.displayName });
      });
    }
    this.choosableValueOptions = choosableValueOptions;
    this.loadingOptions = false;
  }

  private async loadCountryListToOptions() {
    this.isCountryFilter = true;
    this.loadingOptions = false;
  }

  private loadValueFromIntegrationConfig(): void {
    const appName = this.integrationsService.getAppName(this.audienceFilter.app);
    const integration = this.integrationsService.activeIntegrationsBS.value?.find((i) => i.type === appName);
    if (integration?.data?.group) {
      this.choosableValueOptions = [{ key: integration.data.group, value: integration.data.group }];
      this.filterValueString = integration.data.group;
      this.integrationsService.getSettings(this.audienceFilter).subscribe((response: any[]) => {
        const match = response?.find((item) => item.id === integration.data.group);
        if (match) {
          this.choosableValueOptions = [{ key: match.id, value: match.name }];
        }
      });
    }
  }

  private loadAppsOptions() {
    // In custobar tag filter we can't get tags for drop down so just return
    if (this.audienceFilter.app === 'Custobar' && this.isAppTags) {
      return;
    }
    this.loadingOptions = true;
    this.integrationsService.getSettings(this.audienceFilter).subscribe(
      (response: any[]) => {
        this.choosableValueOptions = [];
        if (response) {
          response.forEach((item) => {
            this.choosableValueOptions.push({
              key: item.id,
              value: item.name,
            });
          });
        }
        this.loadingOptions = false;
        this.audienceValueError = false;
        this.filterValueTagString = '';
      },
      () => {
        this.loadingOptions = false;
        this.snackbarService.error('generic.error');
      },
    );
  }

  public loadTagsOptions(): void {
    if (this.isAppTags) {
      this.loadingOptions = true;
      this.integrationsService.getTags(this.audienceFilter, this.filterValueString).subscribe(
        (response: any[]) => {
          this.choosableValueTagOptions = [];
          if (response) {
            response.forEach((item) => {
              this.choosableValueTagOptions.push({
                key: item.id,
                value: item.name,
              });
            });
          }
          this.loadingOptions = false;
          this.audienceValueError = false;
          this.filterValueTagString = '';
        },
        () => {
          this.loadingOptions = false;
          this.snackbarService.error('generic.error');
        },
      );
    }
  }

  private loadWeatherAppsOptions(): void {
    this.audienceService.getAllWeatherApps().subscribe(
      (weatherApps: WeatherAppsKeyValuePair[]) => {
        this.weatherApps = weatherApps;
      },
      (error) => {
        this.snackbarService.error('Failed to load weather apps');
        console.error('Failed to load weather apps', error);
      },
    );
  }

  public onTimeFrameChange(value: string): void {
    this.timeFrame = value;
    this.audienceFilter.weatherProperties.daysForward = null;
    this.audienceFilter.weatherProperties.specificDay = null;
    this.audienceFilter.weatherProperties.checkToday = false;
    this.audienceFilter.weatherProperties.checkTomorrow = false;
    this.audienceFilter.weatherProperties.checkNextWeekend = false;
    this.audienceFilter.weatherProperties.inDays = null;
    switch (value) {
      case TimeFrame.DaysForward:
        if (!this.audienceFilter.weatherProperties.daysForward) {
          this.audienceFilter.weatherProperties.daysForward = 1;
        }
        break;
      case TimeFrame.SpecificDay:
        if (!this.audienceFilter.weatherProperties.specificDay) {
          this.audienceFilter.weatherProperties.specificDay = new Date();
        }
        break;
      case TimeFrame.InDays:
        if (!this.audienceFilter.weatherProperties.inDays) {
          this.audienceFilter.weatherProperties.inDays = 1;
        }
        break;
      default:
        this.audienceFilter.weatherProperties[value] = true;
        break;
    }
  }

  public validateWeather(): boolean {
    if (!this.timeFrame) {
      return false;
    }
    if (this.audienceFilter.weatherProperties.weatherType === WeatherTypeModel.Temperature) {
      this.filterInputType = AudienceFilterInputType.Temperature;
    } else {
      this.filterInputType = AudienceFilterInputType.Weather;
    }
    if (
      this.timeFrame === TimeFrame.DaysForward &&
      (!this.audienceFilter.weatherProperties.daysForward ||
        this.audienceFilter.weatherProperties.daysForward < 1 ||
        this.audienceFilter.weatherProperties.daysForward > 10)
    ) {
      return false;
    }
    if (this.timeFrame === TimeFrame.SpecificDay && !this.audienceFilter.weatherProperties.specificDay) {
      return false;
    }
    if (
      this.timeFrame === TimeFrame.InDays &&
      (!this.audienceFilter.weatherProperties.inDays ||
        this.audienceFilter.weatherProperties.inDays < 1 ||
        this.audienceFilter.weatherProperties.inDays > 10)
    ) {
      return false;
    }
    if (
      (this.audienceFilter.weatherProperties.minTemperature !== null &&
        this.audienceFilter.weatherProperties.maxTemperature !== null &&
        this.audienceFilter.weatherProperties.maxTemperature < this.audienceFilter.weatherProperties.minTemperature) ||
      (this.audienceFilter.weatherProperties.minTemperature === null &&
        this.audienceFilter.weatherProperties.maxTemperature !== null) ||
      (this.audienceFilter.weatherProperties.minTemperature !== null &&
        this.audienceFilter.weatherProperties.maxTemperature === null)
    ) {
      return false;
    }
    if (
      (this.audienceFilter.weatherProperties.customLongitude === null &&
        this.audienceFilter.weatherProperties.customLatitude !== null) ||
      (this.audienceFilter.weatherProperties.customLongitude !== null &&
        this.audienceFilter.weatherProperties.customLatitude === null)
    ) {
      return false;
    }
    if (
      (this.audienceFilter.weatherProperties.minWindspeed !== null &&
        this.audienceFilter.weatherProperties.maxWindspeed !== null &&
        (this.audienceFilter.weatherProperties.maxWindspeed < this.audienceFilter.weatherProperties.minWindspeed ||
          this.audienceFilter.weatherProperties.minWindspeed < 0 ||
          this.audienceFilter.weatherProperties.maxWindspeed < 0)) ||
      (this.audienceFilter.weatherProperties.minWindspeed === null &&
        this.audienceFilter.weatherProperties.maxWindspeed !== null &&
        this.audienceFilter.weatherProperties.maxWindspeed < 0) ||
      (this.audienceFilter.weatherProperties.minWindspeed !== null &&
        this.audienceFilter.weatherProperties.maxWindspeed === null &&
        this.audienceFilter.weatherProperties.minWindspeed < 0)
    ) {
      return false;
    }
    if (
      this.filterInputType === AudienceFilterInputType.Weather &&
      this.audienceFilter.weatherProperties.weatherType == null
    ) {
      return false;
    }
    if (
      this.filterInputType === AudienceFilterInputType.Temperature &&
      (this.audienceFilter.weatherProperties.minTemperature == null ||
        this.audienceFilter.weatherProperties.maxTemperature == null)
    ) {
      return false;
    }
    return true;
  }

  public onWeatherChange(weatherType): void {
    if (weatherType.value === WeatherTypeModel.Temperature) {
      this.audienceFilter.type = AudienceFilterType.Temperature;
    } else {
      this.audienceFilter.type = AudienceFilterType.Weather;
    }
  }

  public filterFrequencyOperatorUpdated(frequencyOperator: AudienceFilterFrequencyOperator): void {
    this.frequencyOperator = frequencyOperator;
  }

  public openSubMenu(event: MouseEvent): void {
    event.stopPropagation();
  }

  public closeSubMenuTimeFrame(event: MouseEvent, timeframeOperator: AudienceFilterTimeOperator) {
    this.timeframeOperator = timeframeOperator;
    event.stopPropagation();
    if (this.timeframeOperator === this.AudienceFilterTimeOperators.AnyTime) {
      this.menuTriggers.toArray().forEach((trigger) => trigger.closeMenu());
    }
    if (this.audienceFilter.type === AudienceFilterType.Goals && this.menuTriggers.last) {
      this.menuTriggers.last.closeMenu();
    } else if (this.audienceFilter.type === AudienceFilterType.Purchase) {
      this.menuTriggers.toArray()[3].closeMenu();
    }
  }

  public closeSubMenuSales(event: MouseEvent, salesOperator) {
    this.salesOperator = salesOperator;
    event.stopPropagation();
    if (this.salesOperator === this.AudienceFilterSalesOperator.Any) {
      this.menuTriggers.toArray().forEach((trigger) => trigger.closeMenu());
    }
    if (this.menuTriggers.last) {
      this.menuTriggers.last.closeMenu();
    }
  }

  get isTemperatureRangeInvalid(): boolean {
    if (
      this.audienceFilter.weatherProperties.maxTemperature !== null &&
      this.audienceFilter.weatherProperties.minTemperature !== null &&
      this.audienceFilter.weatherProperties.maxTemperature < this.audienceFilter.weatherProperties.minTemperature
    ) {
      return true;
    }
  }

  get isWindRangeInvalid(): boolean {
    if (
      this.audienceFilter.weatherProperties.maxWindspeed !== null &&
      this.audienceFilter.weatherProperties.minWindspeed !== null &&
      this.audienceFilter.weatherProperties.maxWindspeed < this.audienceFilter.weatherProperties.minWindspeed
    ) {
      return true;
    }
  }

  public get isTypeGoalOrPurchase() {
    return this.isTypeGoal || this.isTypePurchase;
  }

  public get isTypePurchaseOperatorEquals() {
    return this.isTypePurchase && this.filterOperator !== AudienceFilterOperator.NotEquals;
  }

  public get isTypeGoalOperatorEquals() {
    return this.isTypeGoal && this.isFrequencyOperatorAllowed;
  }

  private get isFrequencyOperatorAllowed() {
    return (
      this.filterOperator === AudienceFilterOperator.Equals || this.filterOperator === AudienceFilterOperator.AnyOf
    );
  }

  public get isFrequencyHidden() {
    return (
      (this.isTypePurchase && !this.isFrequencyOperatorAllowed) || (this.isTypeGoal && !this.isFrequencyOperatorAllowed)
    );
  }

  public get isTypePurchase() {
    return this.audienceFilter.type === this.AudienceFilterTypes.Purchase;
  }

  public get isTypeGoal() {
    return this.audienceFilter.type === this.AudienceFilterTypes.Goals;
  }

  get getDisabledSubmit(): boolean {
    if (this.filterInputType === AudienceFilterInputType.TextField) {
      if (!this.filterValueString && this.isAppTags) {
        return true;
      }
    } else {
      if (!this.choosableValueTagOptions?.length && this.filterValueString && this.isAppTags) {
        return true;
      }
    }
    return (
      (this.audienceFilter.type === AudienceFilterType.Weather ||
        this.audienceFilter.type === AudienceFilterType.Temperature) &&
      !this.validateWeather()
    );
  }

  get isAppTags(): boolean {
    return this.audienceFilter.type === AudienceFilterType.AppTags;
  }

  public disableOptionIfBooleanDropdown() {
    if (this.filterInputType === AudienceFilterInputType.BooleanDropdown && this.audienceFilters?.length) {
      const targetFilter = this.audienceFilters.find((filter) => filter.type === this.audienceFilter.type);
      if (targetFilter) {
        this.audienceFilter.disabledOption = targetFilter.value === 'true' ? 'no' : 'yes';
        this.filterValueString = this.audienceFilter.disabledOption === 'yes' ? 'false' : 'true';
      }
    }
  }

  ngOnDestroy(): void {
    this.subscriptionArray.push(this.getAccountSub);
    this.subscriptionArray.forEach((subs) => subs && subs.unsubscribe());
  }

  public partialNote: Note = {
    message: 'audience.filter-config.weatherDisclaimer-message',
    type: 'generic.note',
  };
}
